import React, { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
// Relative, not "@/...": a pure module imported for REAL under Jest (see its header).
import { apiErrorCode } from "../lib/apiErrors";
import { tenantHref } from "@/lib/tenantPath";
import { checkoutErrorMessage } from "@/lib/checkoutErrors";
import { currentRef } from "../lib/affiliateRef";
// --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
import { currentPromotionRef } from "../lib/promotionRef";
// --- crm-module END ---
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import { useTenant } from "@/lib/TenantContext";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import PromoField from "@/components/checkout/PromoField";
import GiftCardField from "@/components/checkout/GiftCardField";
// Relative, not "@/...": a real module under Jest, no alias there.
import MarketingOptIn from "../components/consent/MarketingOptIn";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowLeft, Lock, Loader2, FileQuestion, BookOpen, Trophy } from "lucide-react";
// Relative, not "@/...": this infra (useFieldErrors/FieldError/lib/validation) is
// imported for REAL under Jest (no "@" alias there) — same convention as
// hooks/useFieldErrors.js's own header explains.
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { emailField } from "../lib/validation";
// Relative, for the same reason: the ONE pre-payment breakdown (BUG-048) and the hook
// that asks the server for it.
import useCheckoutQuote from "../lib/useCheckoutQuote";
import PriceBreakdown from "../components/checkout/PriceBreakdown";
// Relative: the shared quiz player and the testid registry resolve for real under Jest.
import QuizView from "../components/learn/QuizView";
import { pluralKey } from "../lib/pluralKey";
// Relative: pure module. The pathway step a course was opened from (contract §10.5).
import { pathwayReturn, stepPath } from "../pathways/learnerModel";

/**
 * LearnPlayer — Public LMS player at /t/:slug/learn/:courseId
 * - Renders a course's lessons
 * - Tracks progress (completed lessons)
 * - Paid course: sign-in gate + redirect to the Stripe checkout
 * - THE STUDENT IS THE SIGNED-IN ACCOUNT (SHEET-235). Every student call (course read,
 *   enrollment, enroll, progress, quiz) carries the session cookie and NO address: the
 *   server reads the student from the session. The player used to keep the buyer's address
 *   in localStorage and send that instead — so a buyer signed in from another browser (or an
 *   Incognito window) met the paywall of a course "My courses" listed as theirs, and the
 *   previous person on a shared browser stayed "the student" after signing out.
 * - Quiz: multiple-choice rendering, submission, scoring, retry
 * - Mandatory gating: a mandatory lesson locks everything after it
 *
 * BUG-006: the page used to draw its own white bar (a back-link and the course title) and
 * nothing else — no navigation, no footer, on any of the six templates. The practice's
 * chrome is now resolved from `tenant.site_template` like every other interior page
 * (templates/ThemedPageLayout.jsx), in BOTH states of the page: there is no focus mode here
 * to protect (nothing ever hid the bar, and a lesson is a card in a two-column page, not a
 * fullscreen surface), so a chrome-less branch would be a mode this player does not have —
 * and the enrolled state is precisely where the student finishes a lesson and needs a way
 * back into the site. The back-link survives as page content, below the site header.
 */
export default function LearnPlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const t = useT("admin");
  const tp = useT("public");
  const tc = useT("common");
  const { lang } = useLanguage();
  // Field-level errors resolve through the "common" namespace: lib/validation.js's
  // messages are common.validation.* keys, shared by every form that adopts this recipe.
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const { logout, refresh: refreshAuth, loading: authLoading } = useAuth();
  const { user, ensureAuthed, authDialog } = useRequireAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  // The address TYPED by a visitor who is not signed in yet — handed to `ensureAuthed` so
  // the sign-in dialog can compare it with the account that signs in. It is never an
  // identity: once signed in the field shows `user.email`, locked.
  const [email, setEmail] = useState("");
  // BUG-019 — promo code for a PAID course. The server re-decides it at payment
  // (400 PROMO_INVALID); what this page holds is a preview, never a price.
  const [promoCode, setPromoCode] = useState("");
  // BUG-020 — a gift card for a PAID course. Unlike the promo it does not change the
  // price, only who pays which part of it; the server spends it at checkout and gives
  // it back if the session cannot be created.
  const [giftCardCode, setGiftCardCode] = useState("");
  // The OPTIONAL "send me news and offers" box of a PAID course's checkout — unticked
  // until the buyer ticks it (CASL); recorded by the server only once the sale is paid.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // WHO the reads below are for: the signed-in account, or "" for an anonymous visitor.
  // A change of account (sign-in, sign-out, another person signing in) re-reads the course
  // and the enrolment. `reload` forces a re-read when the account does not change (after a
  // free enrolment, or while a payment's fulfilment settles).
  const student = user?.email || "";
  const [reload, setReload] = useState(0);
  const [enrollment, setEnrollment] = useState(null);
  const [activeLesson, setActiveLesson] = useState(0);
  const [busy, setBusy] = useState(false);
  // A pathway's course step opens this player as `?pathway=<pid>&step=<sid>`
  // (docs/pathways-module/pathways-api-contract.md §10.5): the student gets a way back to
  // that step. Read once, from the URL the player was opened at.
  const fromPathway = useMemo(() => pathwayReturn(window.location.search), []);

  // Lesson content is served to an enrolled student only, and the server decides who that
  // is from the session. The enrolment is READ server-side instead of living in React state
  // alone — otherwise a returning student (the "Continue" link of the client portal) meets
  // the paywall again and re-buys a course they already paid for. Nothing is read while the
  // session itself is still being resolved: reading then would show a signed-in buyer the
  // paywall for a moment and read everything twice.
  useEffect(() => {
    if (authLoading) return undefined;
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/learn/courses/${courseId}`),
      // SECONDARY read: its failure must not hide the course (at worst an enrolled
      // student is shown the paywall, never the other way round). Anonymous visitors have
      // no enrolment to ask about.
      student
        ? api.get("/learn/enrollment", { params: { course_id: courseId } })
            .catch(() => ({ data: { enrolled: false } }))
        : Promise.resolve({ data: { enrolled: false } }),
    ])
      .then(([c, e]) => {
        if (!alive) return;
        setCourse(c.data);
        setEnrollment(e.data?.enrolled
          ? { progress: e.data.progress || [], quiz_results: e.data.quiz_results || {} }
          : null);
      })
      .catch(() => { if (alive) toast.error(tp("learn.courseNotFound")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [courseId, student, authLoading, reload, tp]);

  // Return from the hosted checkout — confirms the enrolment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "1" && params.get("session_id")) {
      api.get(`/checkout/connect/status/${params.get("session_id")}`)
        .then(({ data }) => {
          if (data.status === "paid") {
            // THIS POLL TRIGGERS FULFILMENT, IT DOES NOT WAIT FOR IT. Server-side,
            // `confirm_session` claims the outbox then runs `side_effects.dispatch` as a
            // DETACHED TASK and answers immediately, so "paid" precedes the enrolment. A
            // single re-read lost that race: the buyer still saw "Buy" after paying
            // (observed in a browser on 2026-08-05, one time in two). So we re-read until
            // the enrolment EXISTS, bounded — the same discipline as CheckoutSuccess.jsx,
            // which polls 8 times and has never shown this defect. The poll asks about the
            // SIGNED-IN buyer (the session cookie): the checkout was opened by that account.
            let tries = 0;
            const settle = async () => {
              tries += 1;
              setReload((n) => n + 1);
              if (tries >= 8) return;
              try {
                const { data: e } = await api.get("/learn/enrollment", {
                  params: { course_id: courseId },
                });
                if (e?.enrolled) return;             // the content is unlocked
              } catch (err) {
                /* network, or the session not resolved yet: retry — the money has
                   already been taken */
              }
              setTimeout(settle, 1500);
            };
            settle();
            toast.success(tp("learn.paymentConfirmed"));
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tp, courseId]);

  // WHAT THIS COURSE WILL COST (BUG-048/BUG-054), through the ONE hook every storefront
  // uses. It replaces a local fetch + a hand-rolled table that had two defects: it read
  // the practice's slug out of `window.location.pathname` with a `^/t/<slug>/` regex, so
  // on a real tenant HOST — every practice with a subdomain or a custom domain — the slug
  // was never found and the breakdown was never even requested; and it printed its labels
  // as hardcoded French ("Sous-total", "Total") inside an otherwise translated page.
  // `useTenant().slug` (inside the hook's caller below) is correct in all three hosting
  // contexts, and every label now comes from the locale layer.
  const { slug: tenantSlug } = useTenant();
  const { quote: breakdown, error: breakdownError } = useCheckoutQuote({
    serviceType: "course",
    offeringType: "course",
    offeringId: courseId,
    amountCents: course?.price_cents || 0,
    tenantSlug,
    promoCode,
    enabled: Boolean(course && course.price_cents > 0),
  });

  const lessons = useMemo(() => course?.lessons || [], [course]);

  // Compute the index of the first lesson that is locked because of a mandatory predecessor not completed.
  // Lessons before this index are unlocked; lessons at/after still locked except the gating lesson itself.
  const firstLockedIndex = useMemo(() => {
    const completed = new Set(enrollment?.progress || []);
    for (let i = 0; i < lessons.length; i += 1) {
      const l = lessons[i];
      if (l.mandatory && !completed.has(l.id)) {
        return i + 1; // after this lesson, everything is locked
      }
    }
    return lessons.length;
  }, [lessons, enrollment]);

  const isLocked = (idx) => idx >= firstLockedIndex;

  // BUG-207 — the server states sellability on every course (`payable_online`,
  // `unpayable_reason`), the same law already applied to programmes/products/booking
  // services. A free course is untouched (it is never judged on currency at all — see
  // `_advertised_course`'s server-side docstring), so this only ever blocks a PAID course
  // held in a currency this practice does not price in.
  const blocked = !!course && course.price_cents > 0 && course.payable_online === false;
  // A course with no lessons is not for sale at all, free or paid: the server states it as
  // `unpayable_reason: "no_lessons"` (checked before currency) and refuses the enrolment.
  const noLessons = !!course && course.unpayable_reason === "no_lessons";

  const enroll = async () => {
    // Defense in depth: the Enrol button never renders when `blocked` (see below), but
    // `enroll` refuses it directly too rather than trusting the render path — the same
    // discipline Programmes.jsx's `buy` and Boutique.jsx's `buy` already apply.
    if (blocked || noLessons) return;
    // Validated BEFORE ensureAuthed(): a malformed/blank typed address is this page's
    // own mistake to answer, and must not open the auth dialog first only to discard
    // whatever was typed here the moment it resolves (see `authedEmail` below). Once
    // signed in, the field is disabled/prefilled and there is nothing left to validate.
    if (!user) {
      const errors = validate(z.object({ email: emailField }), { email });
      if (errors) return;
    }
    const u = await ensureAuthed(email);
    if (!u) return;
    // The signed-in account is the buyer; the typed `email` is NOT reused below.
    const authedEmail = u.email;
    setBusy(true);
    try {
      if (course.price_cents > 0) {
        // The hosted checkout. NO FALLBACK: the previous one enrolled the buyer for
        // free the moment checkout failed, so any defect on the payment path gave a paid
        // course away, without authentication. The backend refuses on its own now; here
        // we only follow the URL it returns.
        const { data } = await api.post("/checkout/connect/course", {
          course_id: courseId,
          email: authedEmail,
          origin_url: window.location.origin,
          // The player is mounted at the root on a tenant host (/learn/:courseId) and
          // under /t/:slug/ on the platform host: it is the PAGE that knows where Stripe
          // must send the buyer back, not the backend.
          return_path: window.location.pathname,
          // Attribution (contract §2). This used to read `localStorage` directly, which
          // honoured the v0 bare-string value forever — no expiry, no window, no shape
          // check. `currentRef()` is the one reader: it drops a lapsed referral, rewrites
          // the legacy shape, and answers "" when there is nothing to send.
          ref: currentRef(),
          // --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
          promotion_id: currentPromotionRef() || undefined,
          // --- crm-module END ---
          // Omitted entirely when nothing was typed: the server reads an absent code as
          // "no promo", while "" would be a value it has to interpret.
          ...(promoCode ? { promo_code: promoCode } : {}),
          ...(giftCardCode ? { gift_card_code: giftCardCode } : {}),
          marketing_opt_in: marketingOptIn,
        });
        if (data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.error(t("learn.error"));
        return;
      } else {
        // The student is the session; the server takes the address and name from it.
        await api.post(`/learn/enroll?course_id=${courseId}`);
      }
      // The enrolment is no longer fabricated locally: the server is re-read, since it is
      // also the one deciding whether the lesson CONTENT is served.
      setReload((n) => n + 1);
      toast.success(tp("learn.enrollConfirmed"));
    } catch (e) {
      // BUG-019 — `checkoutErrorMessage` maps the money path's stable codes (PROMO_INVALID
      // among them) onto an i18n key, and falls back to `extractApiError` only for refusals
      // that predate the codes. It also flattens a `{code, message}` detail, which a bare
      // `detail || fallback` would have handed to a toast as "[object Object]". `tp`: the
      // keys live in the "public" namespace.
      toast.error(checkoutErrorMessage(e, tp, t("learn.error")));
    } finally {
      setBusy(false);
    }
  };

  // A student call refused because the session has ended (401 SIGN_IN_REQUIRED) is said
  // as such — never as the generic "error" — and the account is re-asked: the session is
  // gone, so `user` becomes null and the reads above show the storefront until the student
  // signs in again.
  const studentCallError = (e) => {
    if (apiErrorCode(e) === "SIGN_IN_REQUIRED") {
      toast.error(tp("learn.signInRequired"));
      refreshAuth?.();
      return;
    }
    toast.error(extractApiError(e, t("learn.error")));
  };

  const markComplete = async (lessonId) => {
    try {
      await api.post(`/learn/progress?course_id=${courseId}&lesson_id=${lessonId}`);
      setEnrollment((e) => ({ ...e, progress: [...(e?.progress || []), lessonId] }));
      toast.success(tp("learn.lessonValidated"));
    } catch (e) {
      studentCallError(e);
    }
  };

  const submitQuiz = async (lesson, answers, openAnswers) => {
    setBusy(true);
    try {
      const { data } = await api.post("/learn/quiz-submit", {
        course_id: courseId,
        lesson_id: lesson.id,
        answers,
        open_answers: openAnswers,
      });
      setEnrollment((e) => ({
        ...e,
        progress: data.passed ? [...(e?.progress || []), lesson.id] : (e?.progress || []),
        quiz_results: { ...(e?.quiz_results || {}), [lesson.id]: { score: data.score, passed: data.passed } },
      }));
      if (data.passed) toast.success(`${t("learn.quizPassed")} ${data.score}%`);
      else toast.error(`${t("learn.quizFailed")} ${data.score}% — minimum : ${data.threshold}%`);
      return data;
    } catch (e) {
      studentCallError(e);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Both terminal states keep the site around them: a spinner or a "no such course" line on
  // a blank page is a dead end, and the second one is exactly where a visitor needs the nav.
  if (loading) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-eh-muted" />
        </section>
      </ThemedPageLayout>
    );
  }
  if (!course) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 px-6 text-center" data-testid="learn-course-not-found">
          {tp("learn.courseNotFound")}
        </section>
      </ThemedPageLayout>
    );
  }

  const lesson = lessons[activeLesson];
  const completed = new Set(enrollment?.progress || []);
  const free = course.price_cents === 0;
  const enrolled = !!enrollment;
  const lockedCurrent = enrolled && isLocked(activeLesson);
  // BUG-225(b) — the "N/M completed" counter and its progress bar are about LESSONS,
  // not quizzes: `lessons` (above) deliberately stays the mixed content+quiz array
  // because it is also the identity source for the sidebar's index-keyed navigation,
  // `firstLockedIndex` and `isLocked` (index identity must not move). The counter reads
  // this separate, content-only view instead of filtering `lessons` itself — and filters
  // `completed` the same way, so a passed quiz (which also lands in `enrollment.progress`,
  // see `submitQuiz` above) cannot inflate the numerator past the lesson-only denominator.
  const contentLessons = lessons.filter((l) => l.lesson_type !== "quiz");
  const completedContentCount = contentLessons.filter((l) => completed.has(l.id)).length;
  // SHEET-225 — a quiz is not a lesson, so it takes no lesson number: content lessons are
  // numbered 1..N among themselves (id -> ordinal), while `lessons` keeps its mixed order
  // for the index-keyed navigation and locking above.
  const lessonNumberById = new Map(contentLessons.map((l, n) => [l.id, n + 1]));

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-16" data-testid="learn-player">
        {authDialog}
        {/* In-page breadcrumb bar, NOT a header: the site's own header is above it. */}
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-4">
          {/* A real destination, not navigate(-1): after the Stripe redirect the previous
              history entry is Stripe's page (or nothing), so "back" must name the
              catalogue — /learn on this host, tenant-prefixed in path mode. */}
          <button
            onClick={() => navigate(tenantHref("/learn"))}
            data-testid="learn-back-to-catalogue"
            className="flex items-center gap-2 text-eh-muted hover:text-eh-ink text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> {tc("actions.back")}
          </button>
          <div className="font-sol font-semibold">{course.title}</div>
          {fromPathway ? (
            <Link
              to={tenantHref(stepPath(fromPathway.pathwayId, fromPathway.stepId))}
              data-testid="learn-back-to-pathway"
              className="flex items-center gap-2 text-eh-muted hover:text-eh-ink text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> {tp("pathways.course.backToPathway")}
            </Link>
          ) : (
            <div className="w-20" />
          )}
        </div>

        {!enrolled ? (
          <div className="max-w-2xl mx-auto p-12 text-center" data-testid="enroll-prompt">
            {course.cover_url && <img src={course.cover_url} alt="" className="rounded-3xl mb-8 max-h-80 mx-auto" />}
            <h1 className="font-display text-4xl mb-3">{course.title}</h1>
            <p className="text-eh-muted mb-8">{course.description}</p>
            <div className="bg-white rounded-2xl border border-eh-border-soft p-6 max-w-md mx-auto">
              <div className="mb-4">
                <div className="text-3xl font-display text-eh-ink">
                  {free ? <span className="text-emerald-600">{t("learn.free")}</span> : `${(course.price_cents / 100).toFixed(2)} ${course.currency.toUpperCase()}`}
                </div>
                <div className="text-xs text-eh-muted mt-1">
                  <span data-testid="enroll-lesson-count">{tc(pluralKey(lang, lessons.length, "count.lessons"), { n: lessons.length })}</span>
                  {lessons.some((l) => l.lesson_type === "quiz") && ` · ${tp("learn.withQuiz")}`}
                </div>
              </div>
              {!free && (
                <div className="mb-3">
                  <PriceBreakdown
                    quote={breakdown}
                    error={breakdownError}
                    currency={course.currency}
                    testId="checkout-breakdown"
                  />
                </div>
              )}
              {/* BUG-207 — a paid course held in a currency this practice does not price
                  in (`payable_online === false`) is refused by `quote_hosted_order` at
                  the click; the buyer must learn that from THIS card, not from the
                  refusal. Reuses the same "cannot be paid online" copy Programmes.jsx and
                  Boutique.jsx already show for the identical server-stated reason. */}
              {noLessons ? (
                <p className="text-sm italic text-eh-muted" data-testid="enroll-not-available">
                  {tp("learn.notAvailableYet")}
                </p>
              ) : blocked ? (
                <p className="text-sm italic text-eh-muted" data-testid="enroll-unavailable">
                  {tp("checkout.error.invalidPrice")}
                </p>
              ) : (
                <>
                  {/* NO CONNECT-ONBOARDING GATE. The buy flow is offered whatever the
                      practice's own Stripe account looks like: Solunary is the merchant of
                      record, the charge lands on the platform balance, and the practitioner's
                      share waits in the ledger until their payout destination exists. */}
                  <input
                    type="email"
                    value={user ? user.email : email}
                    onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }}
                    placeholder={t("learn.emailPlaceholder")}
                    data-testid="enroll-email"
                    disabled={!!user}
                    className={`w-full border rounded-lg px-3 h-11 text-sm mb-1.5 disabled:opacity-70 ${fieldErrors.email ? invalidInputClass : ""}`}
                  />
                  <FieldError message={fieldErrors.email ? tc(fieldErrors.email) : ""} testId="enroll-email-error" />
                  {user && (
                    <p className="text-xs text-eh-muted mb-3">
                      {tp("clientAuth.signedInAs", { email: user.email })}{" "}
                      <button type="button" onClick={() => logout()} className="underline">
                        {tp("clientAuth.notYou")}
                      </button>
                    </p>
                  )}
                  {/* PAID COURSES ONLY: a free enrollment has nothing to discount, and
                      nothing to pay with a card either. */}
                  {!free && (
                    <div className="mb-3 space-y-3">
                      <PromoField
                        kind="course"
                        value={promoCode}
                        onChange={setPromoCode}
                        currency={course.currency}
                        disabled={busy}
                      />
                      {/* This page knows exactly which course is being bought, so it can
                          quote the card against THE TAXED TOTAL the server just quoted —
                          not against the shelf price, which understated what the card
                          covers. `0` while the quote is unknown, which puts the field in
                          its balance-only mode rather than inventing a residual. */}
                      <GiftCardField
                        value={giftCardCode}
                        onChange={setGiftCardCode}
                        amountCents={breakdown?.total_charged_cents || 0}
                        currency={course.currency}
                        disabled={busy}
                      />
                      <MarketingOptIn
                        form="course"
                        checked={marketingOptIn}
                        onChange={setMarketingOptIn}
                        disabled={busy}
                      />
                    </div>
                  )}
                  {/* One paid path only: the hosted checkout via `enroll`
                      (POST /checkout/connect/course). The Square rail was removed on
                      2026-08-04 along with its tokenisation component. */}
                  <button
                    onClick={enroll}
                    // Content validity (a blank/malformed typed email) is judged on click
                    // by enroll() itself — only the in-flight request is a structural
                    // blocker here (house rule: never disable for content validity).
                    disabled={busy}
                    data-testid="enroll-submit"
                    className="w-full bg-eh-ink text-eh-bg rounded-lg h-11 font-medium hover:bg-eh-ink/90 disabled:opacity-50"
                  >
                    {busy ? "..." : free ? t("learn.enrollFree") : t("learn.enrollPrice")}
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 max-w-7xl mx-auto p-6 gap-6">
            {/* Sidebar lessons */}
            <aside className="lg:col-span-1 bg-white rounded-2xl border border-eh-border-soft p-4 h-fit">
              <h3 className="font-display text-lg mb-3">{tp("learn.courseContents")}</h3>
              <div className="space-y-1">
                {lessons.map((l, i) => {
                  const done = completed.has(l.id);
                  const locked = isLocked(i);
                  const isQuiz = l.lesson_type === "quiz";
                  return (
                    <button
                      key={l.id}
                      onClick={() => !locked && setActiveLesson(i)}
                      disabled={locked}
                      data-testid={`lesson-tab-${i}`}
                      className={`w-full text-left p-2 rounded-lg text-sm flex items-center gap-2 transition ${
                        i === activeLesson ? "bg-eh-cream" : locked ? "opacity-40 cursor-not-allowed" : "hover:bg-eh-cream/50"
                      }`}
                    >
                      {locked ? (
                        <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      ) : done ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-eh-muted flex-shrink-0" />
                      )}
                      {isQuiz ? (
                        <span className="truncate flex-1 inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[11px] font-medium flex-shrink-0">
                            <FileQuestion className="w-3 h-3" aria-hidden="true" /> {t("learn.quiz")}
                          </span>{" "}
                          <span className="truncate">{l.title || <em className="text-eh-muted">{tp("learn.emptyQuizTitle")}</em>}</span>
                        </span>
                      ) : (
                        <span className="truncate flex-1">{lessonNumberById.get(l.id)}. {l.title || <em className="text-eh-muted">{tp("learn.emptyLessonTitle")}</em>}</span>
                      )}
                      {l.mandatory && <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" title={t("learn.mandatory")} />}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-eh-border-soft text-xs text-eh-muted">
                {completedContentCount}/{contentLessons.length} {contentLessons.length > 1 ? tp("learn.completedPlural") : tp("learn.completedSingular")}
              </div>
              <div className="mt-2 h-1.5 bg-eh-cream rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${contentLessons.length ? (100 * completedContentCount / contentLessons.length) : 0}%` }}
                />
              </div>
            </aside>

            {/* Lesson content. An <article>, not a <main>: the page's <main> is the layout's,
                and a document may carry only one. */}
            <article className="lg:col-span-3 bg-white rounded-2xl border border-eh-border-soft p-8" data-testid="lesson-content">
              {!lesson ? (
                <div className="text-center text-eh-muted py-12">{tp("learn.selectLesson")}</div>
              ) : lockedCurrent ? (
                <div className="text-center py-16" data-testid="lesson-locked">
                  <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="font-display text-2xl mb-2">{t("learn.locked")}</h3>
                  <p className="text-eh-muted">{t("learn.lockedDesc")}</p>
                </div>
              ) : lesson.lesson_type === "quiz" ? (
                <CourseQuizLesson
                  lesson={lesson}
                  completed={completed.has(lesson.id)}
                  lastResult={enrollment?.quiz_results?.[lesson.id]}
                  onSubmit={(answers, openAnswers) => submitQuiz(lesson, answers, openAnswers)}
                  busy={busy}
                />
              ) : (
                <ContentView
                  lesson={lesson}
                  number={lessonNumberById.get(lesson.id)}
                  completed={completed.has(lesson.id)}
                  onMarkComplete={() => markComplete(lesson.id)}
                />
              )}
            </article>
          </div>
        )}
      </section>
    </ThemedPageLayout>
  );
}

// ===== Content Lesson =====
function ContentView({ lesson, number, completed, onMarkComplete }) {
  const t = useT("admin");
  return (
    <>
      <div className="text-xs uppercase tracking-wider text-eh-muted mb-2 inline-flex items-center gap-2">
        <BookOpen className="w-3 h-3" /> {t("learn.lesson")} {number}
        {lesson.mandatory && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{t("learn.mandatory")}</span>}
      </div>
      <h2 className="font-display text-3xl mb-4">{lesson.title}</h2>
      {lesson.video_url && (
        <div className="aspect-video mb-6 rounded-2xl overflow-hidden bg-black">
          {lesson.video_url.includes("youtube") || lesson.video_url.includes("vimeo") ? (
            <iframe src={lesson.video_url} title={lesson.title} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
          ) : (
            <video src={lesson.video_url} controls className="w-full h-full" />
          )}
        </div>
      )}
      {lesson.body && <div className="prose prose-eh max-w-none whitespace-pre-wrap mb-8">{lesson.body}</div>}
      {!completed && (
        <button
          onClick={onMarkComplete}
          data-testid="mark-complete"
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6 py-2.5 font-medium inline-flex items-center gap-2"
        >
          <CheckCircle2 className="w-4 h-4" /> {t("learn.markComplete")}
        </button>
      )}
      {completed && (
        <div className="text-emerald-600 font-medium inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {t("learn.completed")}
        </div>
      )}
    </>
  );
}

// ===== Quiz Lesson =====
/**
 * A course quiz lesson: the lesson's own header (threshold, mandatory badge, title, the
 * "already passed" note) above the shared quiz player (components/learn/QuizView.jsx).
 * The header is rendered INSIDE the quiz root so the quiz page keeps its heading.
 */
function CourseQuizLesson({ lesson, completed, lastResult, onSubmit, busy }) {
  const t = useT("admin");
  const tp = useT("public");
  const header = (shown) => (
    <>
      <div className="text-xs uppercase tracking-wider text-eh-muted mb-2 inline-flex items-center gap-2">
        <FileQuestion className="w-3 h-3" /> {tp("learn.quizThreshold", { n: lesson.pass_threshold ?? 80 })}
        {lesson.mandatory && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{t("learn.mandatory")}</span>}
      </div>
      <h2 className="font-display text-3xl mb-6">{lesson.title || <em className="text-eh-muted">{tp("learn.emptyQuizTitle")}</em>}</h2>
      {completed && !shown && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 inline-flex items-center gap-2 text-emerald-800">
          <Trophy className="w-5 h-5" />
          <span className="font-medium">{tp("learn.quizAlreadyPassed", { score: lastResult?.score ? ` (${lastResult.score}%)` : "" })}</span>
        </div>
      )}
    </>
  );
  return (
    <QuizView
      questions={lesson.quiz_questions || []}
      resetKey={lesson.id}
      header={header}
      disabled={busy}
      onSubmit={({ answers, open_answers }) => onSubmit(answers, open_answers)}
    />
  );
}
